import { useEffect, useRef, useState } from "react";
import ExtraSeats from "./extraSeats";
import SeatsInput from "./seatsInput";
import SeatsInputGlobal from "./seatsInputGlobal";
import GoogleTranslationComponent from "../googleTranslate/GoogleTranslationComponent";
import { useTranslation } from "@/app/i18n/client";
import { CustomDiv, InlineDiv } from "@/common/divs";
import { RegularText, SemiBoldText } from "@/common/texts";
import {
  ActivityTariffAvailability,
  ActivityTariffAvailabilityTariffType,
  ScheduleHoursRangeVm,
} from "@/model";
import {
  ERROR_ACCENT_COLOR,
  FAKE_ID_FOR_EXTRA_AUDIENCE,
  NEUTRO_TITLES_COLOR,
} from "@/utils/constants";

interface TariffLinesProps {
  selectedHour: ScheduleHoursRangeVm;
  tariff: {
    tariffType: ActivityTariffAvailabilityTariffType | undefined;
    tariffs: ActivityTariffAvailability[];
  };
  setPrice: (price: string) => void;
  priceRef: React.MutableRefObject<string>;
  price_detail: React.MutableRefObject<any>;
  setDisableBookButton: (value: boolean) => void;
}

const TariffLines: React.FC<TariffLinesProps> = ({
  selectedHour,
  tariff,
  setPrice,
  priceRef,
  price_detail,
  setDisableBookButton,
}) => {
  const { t } = useTranslation();
  const getMaxSeats = () => {
    if (selectedHour.schedule?.partySize) {
      if (selectedHour.schedule?.partySize > 0) {
        return selectedHour.schedule?.partySize;
      }
    }
    if (selectedHour.schedule?.minimumSeatsToReserve) {
      if (selectedHour.schedule?.minimumSeatsToReserve > 0) {
        return (
          selectedHour.schedule?.places ?? tariff.tariffs[0].availableSeats ?? 0
        );
      } else {
        if (tariff.tariffType == "INDIVIDUAL_DETAILED") {
          let sum = 0;
          for (let i = 0; i < tariff.tariffs.length; i++) {
            if (tariff.tariffs[i].availableSeats) {
              sum += tariff.tariffs[i].availableSeats ?? 0;
            }
          }
          return sum;
        } else {
          return tariff.tariffs[0].availableSeats ?? 0;
        }
      }
    } else {
      return tariff.tariffs[0].availableSeats ?? 0;
    }
  };

  const MIN_SEATS = selectedHour.schedule?.minimumSeatsToReserve ?? 0;
  const MAX_SEATS = getMaxSeats();
  const GLOBAL_CAPACITY = tariff.tariffs[0].globalCapacity ?? undefined;
  const [personas, setPersonas] = useState(
    Array.from({ length: tariff.tariffs.length }, () => "0"),
  );
  const [extraSeats, setExtraSeats] = useState("0");
  const extraPrice = useRef(0);

  const calcMaxSeats = (index: number) => {
    let total = MAX_SEATS;
    personas.forEach((p, i) => {
      if (i !== index) {
        total -= parseInt(p);
      }
    });
    return total;
  };

  useEffect(() => {
    let sum = 0;
    let isError = false;
    for (let i = 0; i < personas.length; i++) {
      const personasInt = parseInt(personas[i]);
      //@ts-ignore
      sum += parseFloat(personasInt * (tariff.tariffs[i].priceWithTax ?? 0));
      const minimumSeatsToReserve = tariff.tariffs[i].minimumSeatsToReserve;

      if (minimumSeatsToReserve) {
        if (personasInt != 0) {
          if (minimumSeatsToReserve > personasInt) {
            isError = true;
          }
        }
      }
    }

    if (isError) {
      setDisableBookButton(true);
    } else {
      if (sum > 0) {
        let sum2 = 0;
        for (let i = 0; i < personas.length; i++) {
          if (personas[i] != "") {
            sum2 = parseInt(personas[i]) + sum2;
          }
        }
        if (sum2 < MIN_SEATS) {
          setDisableBookButton(true);
        } else {
          setDisableBookButton(false);
        }
      }
    }

    if (extraSeats == "0") {
      setPrice(sum.toFixed(2));
      priceRef.current = sum.toFixed(2);
    } else {
      const sumaExtra =
        parseFloat(extraPrice.current.toFixed(2)) + parseFloat(sum.toFixed(2));
      setPrice(sumaExtra.toFixed(2).toString());
      priceRef.current = sumaExtra.toFixed(2).toString();
    }

    const auxPriceDetail = [];
    for (let i = 0; i < personas.length; i++) {
      auxPriceDetail.push({
        audienceName: tariff.tariffs[i].audienceName,
        price: tariff.tariffs[i].priceWithTax,
        quantity: personas[i],
        audienceType: tariff.tariffs[i].audienceType,
        audienceId: tariff.tariffs[i].audienceId,
        tariffId: tariff.tariffs[i].id,
      });
    }
    if (extraSeats !== "0") {
      auxPriceDetail.push({
        audienceName: "Extra por individuo",
        price: tariff.tariffs[0].extraPricePerIndividualWithTax,
        quantity: extraSeats,
        audienceType: "GROUP",
        audienceId: FAKE_ID_FOR_EXTRA_AUDIENCE,
        tariffId: tariff.tariffs[0].id,
        imExtraPrice: true,
      });
    }
    price_detail.current = auxPriceDetail;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personas, extraSeats]);

  return (
    <>
      {selectedHour.schedule?.minimumSeatsToReserve ?
        selectedHour.schedule?.minimumSeatsToReserve > 0 && (
          <RegularText
            fontSize="14px"
            style={{
              marginLeft: "5px",
              marginTop: "10px",
            }}
          >
            {t("select_price_lines.minimum_of") +
              " " +
              selectedHour.schedule?.minimumSeatsToReserve}
          </RegularText>
        )
      : null}
      {(
        selectedHour.schedule?.places === 100000 ||
        selectedHour.schedule?.places === 0
      ) ?
        null
      : <RegularText
          fontSize="14px"
          style={{
            marginLeft: "5px",
            marginTop: "10px",
            marginBottom: "10px",
          }}
        >
          {selectedHour.schedule?.places} plazas disponibles
        </RegularText>}
      {tariff.tariffType === "GROUP" && tariff.tariffs[0].groupSize && (
        <RegularText
          fontSize="14px"
          style={{
            marginLeft: "5px",
            marginTop: "10px",
          }}
        >
          Los grupos son de {tariff.tariffs[0].groupSize} plazas
        </RegularText>
      )}
      {tariff.tariffs.map((tariff, index) => (
        <CustomDiv
          key={`${tariff.id}-${index}`}
          style={{
            width: "100%",
            minHeight: "52px",
            alignContent: "center",
          }}
        >
          <InlineDiv style={{ justifyContent: "space-between" }}>
            <InlineDiv className="calendar-line-tariff">
              <div>
                <InlineDiv>
                  <SemiBoldText
                    fontSize="14px"
                    style={{
                      marginLeft: "5px",
                    }}
                  >
                    {tariff.priceWithTaxFormatted}
                  </SemiBoldText>
                  <SemiBoldText
                    fontSize="14px"
                    style={{
                      textIndent: "55px",
                      marginLeft: "-50px",
                    }}
                  >
                    <GoogleTranslationComponent
                      text={
                        tariff.audienceName ?
                          tariff.audienceName.toLowerCase() == "pax" ?
                            "Pasajeros"
                          : tariff.audienceName
                        : ""
                      }
                    />
                  </SemiBoldText>
                  {selectedHour.schedule?.minimumSeatsToReserve ?
                    selectedHour.schedule?.minimumSeatsToReserve == 0 ?
                      tariff.minimumSeatsToReserve ?
                        <RegularText
                          fontSize="12px"
                          style={{
                            minWidth: "63px",
                            marginRight: "10px",
                            marginLeft: "5px",
                            marginTop: "2px",
                            color:
                              parseInt(personas[index]) != 0 ?
                                (
                                  parseInt(personas[index]) <
                                  tariff.minimumSeatsToReserve
                                ) ?
                                  ERROR_ACCENT_COLOR
                                : NEUTRO_TITLES_COLOR
                              : NEUTRO_TITLES_COLOR,
                          }}
                        >
                          {t("select_price_lines.minimum_of") +
                            " " +
                            tariff.minimumSeatsToReserve}
                        </RegularText>
                      : null
                    : tariff.minimumSeatsToReserve ?
                      <RegularText
                        fontSize="12px"
                        style={{
                          minWidth: "63px",
                          marginRight: "10px",
                          marginLeft: "5px",
                          marginTop: "2px",
                          color:
                            parseInt(personas[index]) != 0 ?
                              (
                                parseInt(personas[index]) <
                                tariff.minimumSeatsToReserve
                              ) ?
                                ERROR_ACCENT_COLOR
                              : NEUTRO_TITLES_COLOR
                            : NEUTRO_TITLES_COLOR,
                        }}
                      >
                        {t("select_price_lines.minimum_of") +
                          " " +
                          tariff.minimumSeatsToReserve}
                      </RegularText>
                    : null
                  : null}
                </InlineDiv>
                {tariff.audienceNote ?
                  <RegularText
                    fontSize="12px"
                    style={{
                      marginLeft: "5px",
                    }}
                  >
                    <GoogleTranslationComponent text={tariff.audienceNote} />
                  </RegularText>
                : tariff.note ?
                  <RegularText
                    fontSize="12px"
                    style={{
                      marginLeft: "5px",
                    }}
                  >
                    <GoogleTranslationComponent text={tariff.note} />
                  </RegularText>
                : null}
                {(
                  (tariff.tariffType === "INDIVIDUAL_DETAILED" &&
                    tariff.totalAvailableSeats == 100000) ||
                  tariff.totalAvailableSeats == 0 ||
                  tariff.tariffType === "INDIVIDUAL"
                ) ?
                  null
                : <RegularText
                    fontSize="12px"
                    style={{
                      marginLeft: "5px",
                    }}
                  >
                    {t("select_price_lines.maximum_of") +
                      " " +
                      tariff.totalAvailableSeats}
                  </RegularText>}
              </div>
            </InlineDiv>
            {selectedHour.schedule?.minimumSeatsToReserve ?
              selectedHour.schedule?.minimumSeatsToReserve == 0 ?
                <SeatsInput
                  globalCapacity={GLOBAL_CAPACITY}
                  min={MIN_SEATS}
                  max={
                    tariff.tariffType == "INDIVIDUAL_DETAILED" ?
                      (tariff.totalAvailableSeats ?? 0)
                    : calcMaxSeats(index)
                  }
                  personas={personas}
                  setPersonas={setPersonas}
                  index={index}
                  tariff={tariff}
                  setDisableBookButton={setDisableBookButton}
                />
              : <SeatsInputGlobal
                  globalCapacity={GLOBAL_CAPACITY}
                  min={
                    tariff.minimumSeatsToReserve ?
                      tariff.minimumSeatsToReserve
                    : selectedHour.schedule?.minimumSeatsToReserve
                  }
                  max={
                    tariff.tariffType == "INDIVIDUAL_DETAILED" ?
                      (tariff.totalAvailableSeats ?? 0)
                    : calcMaxSeats(index)
                  }
                  personas={personas}
                  setPersonas={setPersonas}
                  index={index}
                  setDisableBookButton={setDisableBookButton}
                />

            : <SeatsInput
                globalCapacity={GLOBAL_CAPACITY}
                min={MIN_SEATS}
                max={
                  tariff.tariffType == "INDIVIDUAL_DETAILED" ?
                    (tariff.totalAvailableSeats ?? 0)
                  : calcMaxSeats(index)
                }
                personas={personas}
                setPersonas={setPersonas}
                index={index}
                tariff={tariff}
                setDisableBookButton={setDisableBookButton}
              />
            }
          </InlineDiv>
        </CustomDiv>
      ))}
      {(
        tariff.tariffType === "GROUP" &&
        tariff.tariffs[0].extraPricePerIndividual
      ) ?
        tariff.tariffs[0].extraPricePerIndividual > 0 && (
          <CustomDiv
            style={{
              width: "100%",
              height: "52px",
              alignContent: "center",
            }}
          >
            <InlineDiv style={{ justifyContent: "space-between" }}>
              <InlineDiv
                style={{
                  alignContent: "center",
                  height: "52px",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <InlineDiv>
                    <SemiBoldText
                      fontSize="14px"
                      style={{
                        marginLeft: "5px",
                      }}
                    >
                      {
                        tariff.tariffs[0]
                          .extraPricePerIndividualWithTaxFormatted
                      }
                    </SemiBoldText>

                    <SemiBoldText
                      fontSize="14px"
                      style={{
                        marginLeft: "5px",
                      }}
                    >
                      {"Extra por individuo"}
                    </SemiBoldText>
                  </InlineDiv>
                  {tariff.tariffs[0].externalIndividualLimit == 100000 ? null
                  : <RegularText
                      fontSize="12px"
                      style={{
                        marginLeft: "5px",
                      }}
                    >
                      {t("select_price_lines.maximum_of") +
                        " " +
                        tariff.tariffs[0].externalIndividualLimit}
                    </RegularText>}
                </div>
              </InlineDiv>
              <ExtraSeats
                min={MIN_SEATS}
                max={tariff.tariffs[0].externalIndividualLimit ?? 0}
                extraSeats={extraSeats}
                setExtraSeats={setExtraSeats}
                extraPrice={extraPrice}
                linePrice={
                  tariff.tariffs[0].extraPricePerIndividualWithTax ?? 0
                }
              />
            </InlineDiv>
          </CustomDiv>
        )
      : null}
    </>
  );
};

export default TariffLines;
